/**
 * i18next initialization (en / fr / it / da).
 *
 * Locale catalogs live in `src/locales/<locale>/<namespace>.json` and are
 * auto-discovered with a Vite glob — drop in a new namespace JSON (or a new
 * language folder) and it is picked up with no wiring here. The supported
 * locale set is the single source of truth in `@nord-living/shared`.
 *
 * FR/IT/DA strings are model-generated — have a native speaker review them before
 * production (see docs/guides/I18N.md).
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "@nord-living/shared";

import { installZodI18n } from "@/lib/zod-i18n";

/** localStorage key for the anonymous (pre-login) language choice. */
export const LOCALE_STORAGE_KEY = "nord-living-locale";

type CatalogModule = { default: Record<string, unknown> };

const catalogs = import.meta.glob<CatalogModule>("../locales/**/*.json", {
  eager: true,
});

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
const namespaces = new Set<string>();

for (const filePath in catalogs) {
  const match = filePath.match(/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, locale, namespace] = match;
  namespaces.add(namespace);
  resources[locale] ??= {};
  resources[locale][namespace] = catalogs[filePath].default;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    ns: [...namespaces],
    defaultNS: "common",
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      // The LocaleProvider owns persistence so it can also sync the backend.
      caches: [],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

installZodI18n((key, vars) => i18n.t(key, vars));

export default i18n;
